import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { deleteFile, toUploadedFile, uploadImage, type RawFile, type UploadedFile } from '@/shared/api/upload';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import { getListRows } from '@/shared/lib/pagination';
import type { ListResponse } from '@/shared/ui/list/types';

// ---- UTM -------------------------------------------------------------------

export interface UtmValues {
  id?: number;
  utmSource: string;
  utmMedium: string;
  title: string;
  isActive: boolean;
}

export const saveUtm = ({ id, utmSource, utmMedium, title, isActive }: UtmValues) =>
  submitForm(id ? '/utm/update/' : '/utm/create/', {
    id,
    'key[utm_source]': utmSource,
    'key[utm_medium]': utmMedium,
    title,
    is_active: isActive,
  });

export const deleteUtm = (id: number) => submitForm('/utm/delete/', { ids: [id] });

// ---- organizations (loyalty partners) --------------------------------------

export const addOrganization = (v: { title: string; name: string; cashback: string }) =>
  submitForm('/customer/loyalty-organization/add/', { ...v });

export const changeOrganizationStatus = (organizationId: number, isActive: boolean) =>
  submitForm('/customer/loyalty-organization/change-status/', {
    organization_id: organizationId,
    status_id: isActive,
  });

// ---- partners transactions filters -----------------------------------------

export async function fetchPartnerTypeOptions() {
  const res = await postForm<ListResponse>('/customer/loyalty-organization/get-partner-type/', {});
  return normalizeOptions(getListRows(res, ['partner_types']));
}

export async function fetchPartnerOptions(partnerType = '') {
  const res = await postForm<ListResponse>('/customer/loyalty-organization/search/', {
    partner_type: partnerType || undefined,
  });
  return normalizeOptions(getListRows(res, ['organizations']));
}

// ---- partner promotions (promo-actions) ------------------------------------

export type PartnerPromotionEdit = {
  id: number;
  serviceId: number | string;
  statusId: number | string;
  title: string;
  description: string;
  imageId: number | string;
  cityId: number | string;
  /** Unix seconds. */
  startTime: number;
  tariffId: number | string;
  tariffType: string;
  stopMessage: string;
  rejectMessage: string;
};

export const editPartnerPromotion = (v: PartnerPromotionEdit) =>
  submitForm('/service/promotion/edit/', {
    id: v.id,
    service_id: v.serviceId,
    status_id: v.statusId,
    tariff_id: Number(v.tariffId),
    tariff_type: v.tariffType,
    title: v.title,
    description: v.description,
    image_id: v.imageId,
    city_id: Number(v.cityId),
    start_time: v.startTime,
    stop_message: v.stopMessage || undefined,
    reject_message: v.rejectMessage || undefined,
  });

// ---- promotions ------------------------------------------------------------

export const deletePromotion = (id: number) => submitForm('/promotion/delete/', { id });

// ---- promo create / edit ---------------------------------------------------

export type PromoImageField = 'image' | 'icon' | 'logo' | 'blank';

export interface PromoValues {
  title: string;
  description: string;
  countryId: string;
  /** Hex with leading `#` (the API stores it without). */
  color: string;
  headerGradient: string;
  header: string;
  pointX: string;
  pointY: string;
  image: UploadedFile[];
  icon: UploadedFile[];
  logo: UploadedFile[];
  blank: UploadedFile[];
}

export const DEFAULT_PROMO_COUNTRY = '1';

export const EMPTY_PROMO: PromoValues = {
  title: '',
  description: '',
  countryId: DEFAULT_PROMO_COUNTRY,
  color: '#000000',
  headerGradient: '',
  header: '',
  pointX: '',
  pointY: '',
  image: [],
  icon: [],
  logo: [],
  blank: [],
};

const PROMO_BASE = '/billing/billing-promo';

export const uploadPromoImage = (field: PromoImageField) => (file: File) =>
  uploadImage(`${PROMO_BASE}/upload-${field}/`, file);

export const deletePromoImage = (field: PromoImageField) => (id: number) =>
  deleteFile(`${PROMO_BASE}/delete-${field}/`, id);

export interface PromoItem extends Record<string, unknown> {
  id: number;
  title?: string;
  description?: string;
  color?: string;
  header?: string;
  header_gradient?: string;
  point_x?: number | string | null;
  point_y?: number | string | null;
  country?: { id?: number } | null;
  image?: RawFile | RawFile[] | null;
  icon?: RawFile | RawFile[] | null;
  logo?: RawFile | RawFile[] | null;
  blank?: RawFile | RawFile[] | null;
}

export async function fetchPromo(id: number) {
  const res = await postForm<{ promocode?: PromoItem }>(`${PROMO_BASE}/view/`, { id });
  return res.promocode;
}

const toFiles = (raw: RawFile | RawFile[] | null | undefined) => {
  const file = toUploadedFile(raw);
  return file ? [file] : [];
};

export const promoToValues = (item: PromoItem): PromoValues => ({
  title: item.title ?? '',
  description: item.description ?? '',
  countryId: String(item.country?.id ?? ''),
  color: `#${item.color ?? '000000'}`,
  headerGradient: item.header_gradient ?? '',
  header: item.header ?? '',
  pointX: String(item.point_x ?? ''),
  pointY: String(item.point_y ?? ''),
  image: toFiles(item.image),
  icon: toFiles(item.icon),
  logo: toFiles(item.logo),
  blank: toFiles(item.blank),
});

export const savePromo = (id: number | undefined, v: PromoValues) =>
  submitForm<{ success?: number; promocode?: { id: number } }>(
    `${PROMO_BASE}/${id ? 'update' : 'create'}/`,
    {
      id,
      title: v.title,
      description: v.description,
      color: v.color.replace('#', ''),
      header_gradient: v.headerGradient,
      header: v.header,
      point_x: v.pointX,
      point_y: v.pointY,
      image_id: v.image[0]?.id,
      icon_id: v.icon[0]?.id,
      logo_id: v.logo[0]?.id,
      blank_id: v.blank[0]?.id,
      country_id: v.countryId,
    },
  );

// ---- handbook partner (partner users) --------------------------------------

export interface PartnerUser extends Record<string, unknown> {
  id: number;
  name?: string;
  password?: string;
  status_id?: number | boolean;
}

export interface Partner extends Record<string, unknown> {
  id?: number;
  name?: string;
  name_lat?: string;
  external_id?: string | number;
  create_time?: number;
  user_list?: PartnerUser[] | null;
}

export const fetchPartner = (partnerId: number) =>
  postForm<{ partner?: Partner }>('/partners/default/view/', { partner_id: partnerId });

export const savePartnerUser = (
  partnerId: number,
  user: { id?: number; name: string; password: string; isActive: boolean },
) =>
  user.id
    ? submitForm('/partners/users/edit/', {
        user_id: user.id,
        partner_id: partnerId,
        name: user.name,
        password: user.password,
        status_id: user.isActive,
      })
    : submitForm('/partners/users/add/', { partner_id: partnerId, name: user.name, password: user.password });
